import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock, User, RefreshCw, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { api } from '../../services/api';

const URGENCY_CONFIG = {
  urgent: {
    bg: 'bg-red-950/20 border-red-900/40',
    badge: 'bg-red-950/30 text-red-400',
    dot: 'bg-red-500',
  },
  warning: {
    bg: 'bg-orange-950/20 border-orange-900/40',
    badge: 'bg-orange-950/30 text-orange-400',
    dot: 'bg-orange-500',
  },
  info: {
    bg: 'bg-muted/50 border-border',
    badge: 'bg-muted text-muted-foreground',
    dot: 'bg-primary',
  },
};

const ContractExpiryWidget = () => {
  const { t } = useTranslation();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchExpiring = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await api.get('v1/agents/contract-watcher/expiring?days_ahead=30');
      if (res.data?.success) setContracts(res.data.data);
    } catch {
      // silently ignore — widget is non-critical
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchExpiring(); }, []);

  if (loading) return null;
  if (!contracts.length) return null;

  const urgentCount = contracts.filter(c => c.urgency === 'urgent').length;
  const warningCount = contracts.filter(c => c.urgency === 'warning').length;

  return (
    <Card style={{ borderColor: "rgba(245,158,11,0.25)" }}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-orange-950/30">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-foreground">
                Contrats CDD expirants
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {urgentCount > 0 && <span className="text-red-600 font-medium">{urgentCount} urgent{urgentCount > 1 ? 's' : ''}</span>}
                {urgentCount > 0 && warningCount > 0 && ' · '}
                {warningCount > 0 && <span className="text-orange-600">{warningCount} à surveiller</span>}
                {urgentCount === 0 && warningCount === 0 && `${contracts.length} dans 30 jours`}
              </p>
            </div>
          </div>
          <button
            onClick={() => fetchExpiring(true)}
            className="p-1.5 rounded-md hover:bg-muted transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`h-4 w-4 text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-2">
          {contracts.slice(0, 5).map(contract => {
            const cfg = URGENCY_CONFIG[contract.urgency];
            return (
              <div
                key={contract.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${cfg.bg} transition-all`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-2 w-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-medium text-foreground truncate">
                        {contract.name}
                      </span>
                    </div>
                    {contract.department && (
                      <p className="text-xs text-muted-foreground ml-5 truncate">{contract.department}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span className={`text-xs font-semibold ${contract.urgency === 'urgent' ? 'text-red-400' : 'text-orange-400'}`}>
                        J-{contract.days_left}
                      </span>
                    </div>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${cfg.badge}`}>
                      {t(`dashboard.contractUrgency.${contract.urgency}`)}
                    </span>
                  </div>
                  <a href={`/employees/${contract.id}`}>
                    <ChevronRight className="h-4 w-4 text-muted-foreground hover:text-muted-foreground" />
                  </a>
                </div>
              </div>
            );
          })}

          {contracts.length > 5 && (
            <p className="text-xs text-center text-muted-foreground pt-1">
              +{contracts.length - 5} autre{contracts.length - 5 > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ContractExpiryWidget;
